import React, { useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { Config } from "@eagerdog/config";

import { helperService } from "src/services/helper.service";
import { httpService } from "src/services/http.service";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";

import "./PointRedemption.scss";

interface IProps {
  clubId?: string;
  behalfUserId?: string;
  /** Club portion, in dollars, that points can cover. */
  redeemableAmount: number;
  onRedeem(points: number): void;
}

const minimumAmount = 10;

const PointRedemption: React.FC<IProps> = (props) => {
  const [availablePoints, setAvailablePoints] = useState<number>(0);
  const [amountToRedeem, setAmountToRedeem] = useState<string>("");
  const [loadingPoints, setLoadingPoints] = useState<boolean>(false);

  useEffect(() => {
    const loadAvailablePoints = async () => {
      if (!props.clubId || !helperService.isLoggedIn()) {
        setAvailablePoints(0);
        return;
      }

      setLoadingPoints(true);
      try {
        const url = props.behalfUserId
          ? `${Config.web.REACT_APP_API_URL}/api/wallets/users/${props.behalfUserId}/clubs/${props.clubId}/points`
          : `${Config.web.REACT_APP_API_URL}/api/wallets/me/clubs/${props.clubId}/points`;
        const response = await httpService.get(url);
        setAvailablePoints(response.data.points || 0);
      } catch (error) {
        console.error("Error loading available points:", error);
        setAvailablePoints(0);
      } finally {
        setLoadingPoints(false);
      }
    };

    loadAvailablePoints();
  }, [props.clubId, props.behalfUserId]);

  if (!props.clubId || !helperService.isLoggedIn()) {
    return null;
  }

  const pointsValue = (points: number) => points / Constants.wallet.points_to_dollar_ratio;

  const dollarsLabel = (dollars: number) =>
    `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const maxRedeemablePoints = () => {
    if ((props.redeemableAmount || 0) < minimumAmount) {
      return 0;
    }
    const cartMax = Math.floor(props.redeemableAmount * Constants.wallet.points_to_dollar_ratio);
    return Math.min(availablePoints, cartMax);
  };

  const dollarsToPoints = (raw: string) => {
    const dollars = parseFloat(raw);
    if (isNaN(dollars) || dollars <= 0) {
      return 0;
    }
    return Math.round(dollars * Constants.wallet.points_to_dollar_ratio);
  };

  const handleRedeemPoints = (e: React.FormEvent) => {
    e.preventDefault();
    const pointsNum = dollarsToPoints(amountToRedeem);
    const maxPoints = maxRedeemablePoints();
    if (pointsNum <= 0 || pointsNum > availablePoints || pointsNum > maxPoints) {
      return;
    }
    props.onRedeem(pointsNum);
    setAmountToRedeem("");
  };

  const maxPoints = maxRedeemablePoints();
  const maxDollars = pointsValue(maxPoints);

  return (
    <div className="pointRedemption">
      <div className="pointHeader">
        <h3>Redeem Points</h3>
        {loadingPoints ? (
          <span className="loadingPoints">Loading...</span>
        ) : (
          <span className="availablePoints">
            <span className="availableAmount">Available: {dollarsLabel(pointsValue(availablePoints))}</span>
            <span className="availableCount">{availablePoints.toLocaleString("en-US")} points</span>
          </span>
        )}
      </div>

      {availablePoints > 0 && maxPoints > 0 ? (
        <form className="pointForm" onSubmit={handleRedeemPoints}>
          <div className="pointInputGroup">
            <Input
              label="Amount to Redeem"
              id="amountToRedeem"
              type="text"
              inputMode="decimal"
              value={amountToRedeem}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
                  setAmountToRedeem(raw);
                }
              }}
              placeholder={`Max: ${dollarsLabel(maxDollars)}`}
            />
            <Button type="submit" disabled={dollarsToPoints(amountToRedeem) <= 0 || dollarsToPoints(amountToRedeem) > maxPoints}>
              {amountToRedeem && amountToRedeem !== "." ? `Redeem $${amountToRedeem.replace(/\.$/, "")}` : "Redeem"}
            </Button>
          </div>
          <div className="pointInfo">
            <p>• 1,000 points = $1.00</p>
            <p>• Max redeemable: {dollarsLabel(maxDollars)}</p>
            <p>• Points reduce the club's portion only</p>
          </div>
        </form>
      ) : availablePoints === 0 ? (
        <div className="noPoints">
          <p>You don't have any points available for this club.</p>
        </div>
      ) : (
        <div className="noRedeemable">
          <p>Cart total too low for point redemption (minimum $10.00).</p>
        </div>
      )}
    </div>
  );
};

export default PointRedemption;
